import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/project/karuna/components/ui/tabs"; // @ts-ignore - tabs is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import {
  ArrowLeft,
  Calendar,
  MapPin,
  FileText,
  Image as ImageIcon,
  Download,
  Clock,
  Users,
} from "lucide-react";
import { DynamicModuleService, UploadService } from "@/services/APIs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EditActivityDialog } from "@/project/karuna/components/admin/EditActivityDialog";
import { downloadFileWithAuth } from "@/lib/downloadFileWithAuth";
import { toAbsoluteApiUrl, unwrapFileApiRecord } from "@/lib/toAbsoluteApiUrl";
import {
  mapWithConcurrency,
  DEFAULT_API_FETCH_CONCURRENCY,
} from "@/project/karuna/utils/mapWithConcurrency";

const SCHEMA_NAME = "Activity Management";

interface Activity {
  id?: string;
  _id?: string;
  title?: string;
  activitytitle?: string;
  activity_title?: string;
  description?: string;
  startdate?: string;
  start_date?: string;
  enddate?: string;
  end_date?: string;
  location?: string;
  banner?: any;
  banner_url?: string | string[];
  thumbnail_url?: string | string[];
  attachments?: any;
  status?: string;
  [key: string]: any;
}

const ResponsiveBanner = ({
  src,
  fallbackSrc,
  alt,
}: {
  src: string;
  fallbackSrc?: string | null;
  alt?: string;
}) => {
  const [hasError, setHasError] = useState(false);
  const [activeSrc, setActiveSrc] = useState(src);

  useEffect(() => {
    setActiveSrc(src);
    setHasError(false);
  }, [src]);

  return (
    <div className="flex w-full justify-center overflow-hidden rounded-lg">
      {hasError ? (
        <div className="flex min-h-[100px] w-full flex-col items-center justify-center rounded-lg border border-dashed p-4 text-center text-muted-foreground">
          <span className="text-sm font-medium">Image failed to load</span>
        </div>
      ) : (
        <img
          src={activeSrc}
          alt={alt ?? "Banner"}
          onError={() => {
            if (fallbackSrc && activeSrc !== fallbackSrc) {
              setActiveSrc(fallbackSrc);
              return;
            }
            setHasError(true);
          }}
          style={{
            width: "100%",
            height: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            objectPosition: "center",
            display: "block",
          }}
        />
      )}
    </div>
  );
};

const AdminActivityView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bannerImage, setBannerImage] = useState<string | null>(null);
  const [bannerFallbackImage, setBannerFallbackImage] = useState<string | null>(
    null
  );
  const [thumbnailImage, setThumbnailImage] = useState<string | null>(null);

  // Fetch activity data
  const {
    data: activityData,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ["activity", SCHEMA_NAME, id],
    queryFn: async () => {
      try {
        if (!id) {
          console.log("❌ No activity ID provided");
          return null;
        }
        console.log("🔍 Fetching activity with ID:", id);

        // Try getItemById first
        try {
          const item = await DynamicModuleService.getItemById(SCHEMA_NAME, id);
          console.log("✅ Activity data received from getItemById:", item);
          const data = item?.data ?? item?.result ?? item ?? null;
          if (data) {
            return data;
          }
        } catch (getItemError: any) {
          console.warn(
            "⚠️ getItemById failed, trying list fallback:",
            getItemError
          );
        }

        // Fallback: fetch from list and find by ID
        console.log("🔄 Trying fallback: fetching from list...");
        const activities =
          await DynamicModuleService.getGridColItemsAllPages(SCHEMA_NAME);
        console.log("📋 Fetched activities list, searching for ID:", id);

        const foundActivity = activities.find(
          (act: any) =>
            act.id === id ||
            act._id === id ||
            String(act.id) === String(id) ||
            String(act._id) === String(id)
        );

        if (foundActivity) {
          console.log("✅ Found activity in list:", foundActivity);
          return foundActivity;
        }

        console.warn("❌ Activity not found in list");
        return null;
      } catch (error) {
        console.error("❌ Error fetching activity:", error);
        throw error;
      }
    },
    enabled: !!id,
  });

  // Helper function to extract image URL from various formats
  const extractImageUrl = useCallback(
    async (imageValue: any): Promise<string | null> => {
      if (!imageValue) {
        return null;
      }

      // If it's already a URL string
      if (typeof imageValue === "string") {
        if (
          imageValue.startsWith("http://") ||
          imageValue.startsWith("https://")
        ) {
          return imageValue;
        }

        // Check if it's a JSON string (starts with [ or {)
        if (
          imageValue.trim().startsWith("[") ||
          imageValue.trim().startsWith("{")
        ) {
          try {
            const parsed = JSON.parse(imageValue);
            // Recursively resolve the parsed value
            return await extractImageUrl(parsed);
          } catch (e) {
            // If parsing fails, it's not valid JSON, continue as regular string
            console.warn("Failed to parse JSON string:", e);
          }
        }

        // It might be a file ID, fetch the URL from the API
        try {
          const raw = await UploadService.getFileById({
            id: imageValue,
          });
          const fileResponse = unwrapFileApiRecord(raw) as Record<
            string,
            unknown
          > | null;
          const record = Array.isArray(fileResponse)
            ? fileResponse[0]
            : fileResponse;
          if (record && typeof record === "object") {
            const path =
              (record.file_path as string | undefined) ??
              (record.file_path_original as string | undefined) ??
              (record.original_url as string | undefined) ??
              (record.file_path_optimized as string | undefined) ??
              (record.url as string | undefined) ??
              (record.file_path_thumbnail as string | undefined);
            return path ?? null;
          }
          return null;
        } catch (error) {
          console.warn(`Failed to fetch image for ID ${imageValue}:`, error);
          return null;
        }
      }

      // If it's an array (like banner field which is Array<object>)
      if (Array.isArray(imageValue) && imageValue.length > 0) {
        const firstItem = imageValue[0];

        // If array contains strings (file IDs)
        if (typeof firstItem === "string") {
          return extractImageUrl(firstItem);
        }

        // If array contains objects (from CustomFileUpload)
        if (typeof firstItem === "object" && firstItem !== null) {
          // Check for file_path (original high-quality image)
          const originalPath =
            firstItem.file_path ??
            firstItem.file_path_original ??
            firstItem.original_url;
          if (originalPath) {
            return originalPath;
          }

          // Resolve the file ID before accepting a preview URL. Upload records
          // can expose their thumbnail as `url`, while getFileById returns the
          // original 4K asset in `file_path`.
          const fileId = firstItem.id ?? firstItem.file_id ?? firstItem._id;
          if (fileId) {
            const resolvedOriginal = await extractImageUrl(String(fileId));
            if (resolvedOriginal) {
              return resolvedOriginal;
            }
          }

          return (
            firstItem.file_path_optimized ??
            (typeof firstItem.url === "string"
              ? firstItem.url
              : await extractImageUrl(firstItem.url)) ??
            firstItem.file_path_thumbnail ??
            null
          );
        }
      }

      // If it's an object (single object, not array)
      if (
        typeof imageValue === "object" &&
        imageValue !== null &&
        !Array.isArray(imageValue)
      ) {
        // Check for file_path (original high-quality image)
        const originalPath =
          imageValue.file_path ??
          imageValue.file_path_original ??
          imageValue.original_url;
        if (originalPath) {
          return originalPath;
        }

        const fileId = imageValue.id ?? imageValue.file_id ?? imageValue._id;
        if (fileId) {
          const resolvedOriginal = await extractImageUrl(String(fileId));
          if (resolvedOriginal) {
            return resolvedOriginal;
          }
        }

        return (
          imageValue.file_path_optimized ??
          (typeof imageValue.url === "string"
            ? imageValue.url
            : await extractImageUrl(imageValue.url)) ??
          imageValue.file_path_thumbnail ??
          null
        );
      }

      return null;
    },
    []
  );

  // Resolve a browser-friendly derivative for use only when the original file
  // path is unavailable or cannot be served by the API/storage provider.
  const extractFallbackImageUrl = useCallback(
    async (imageValue: any): Promise<string | null> => {
      if (!imageValue) {
        return null;
      }

      if (typeof imageValue === "string") {
        const trimmed = imageValue.trim();
        if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
          try {
            return await extractFallbackImageUrl(JSON.parse(trimmed));
          } catch {
            return null;
          }
        }
        if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
          return null;
        }
        try {
          const raw = await UploadService.getFileById({ id: trimmed });
          const unwrapped = unwrapFileApiRecord(raw);
          const record = Array.isArray(unwrapped) ? unwrapped[0] : unwrapped;
          if (record && typeof record === "object") {
            return (
              (record.file_path_optimized as string | undefined) ??
              (record.file_path_thumbnail as string | undefined) ??
              (record.url as string | undefined) ??
              null
            );
          }
        } catch {
          return null;
        }
        return null;
      }

      const item = Array.isArray(imageValue) ? imageValue[0] : imageValue;
      if (!item || typeof item !== "object") {
        return null;
      }

      const localFallback =
        item.file_path_optimized ?? item.file_path_thumbnail ?? item.url;
      const fileId = item.id ?? item.file_id ?? item._id;
      if (fileId) {
        const apiFallback = await extractFallbackImageUrl(String(fileId));
        if (apiFallback) {
          return apiFallback;
        }
      }
      return typeof localFallback === "string" ? localFallback : null;
    },
    []
  );

  // Process activity first
  const activity: Activity | null = useMemo(() => {
    if (!activityData) {
      console.log("⚠️ No activityData available");
      return null;
    }
    console.log("📝 Processing activity data:", activityData);

    // Parse attachments if it's a JSON string
    let parsedAttachments = activityData.attachments;
    if (
      typeof parsedAttachments === "string" &&
      parsedAttachments.trim().startsWith("[")
    ) {
      try {
        parsedAttachments = JSON.parse(parsedAttachments);
        console.log("✅ Parsed attachments:", parsedAttachments);
      } catch (e) {
        console.warn("⚠️ Failed to parse attachments JSON:", e);
      }
    }

    // Parse banner if it's a JSON string (for processing)
    let parsedBanner = activityData.banner;
    if (
      typeof parsedBanner === "string" &&
      parsedBanner.trim().startsWith("[")
    ) {
      try {
        parsedBanner = JSON.parse(parsedBanner);
      } catch (_e) {
        // Ignore parse errors
      }
    }

    // Parse thumbnail if it's a JSON string
    let parsedThumbnail = activityData.thumbnail;
    if (
      typeof parsedThumbnail === "string" &&
      parsedThumbnail.trim().startsWith("[")
    ) {
      try {
        parsedThumbnail = JSON.parse(parsedThumbnail);
        console.log("✅ Parsed thumbnail:", parsedThumbnail);
      } catch (e) {
        console.warn("⚠️ Failed to parse thumbnail JSON:", e);
      }
    }

    const processed = {
      ...activityData,
      id: activityData.id ?? activityData._id ?? id,
      title:
        activityData.activitytitle ??
        activityData.title ??
        activityData.activity_title ??
        "Untitled Activity",
      attachments: parsedAttachments,
      banner: parsedBanner,
      thumbnail: parsedThumbnail,
    };
    console.log("✅ Processed activity:", processed);
    return processed;
  }, [activityData, id]);

  const [resolvedAttachments, setResolvedAttachments] = useState<any[]>([]);

  // Fetch banner and thumbnail images
  useEffect(() => {
    const fetchMedia = async () => {
      if (!activity) {
        return;
      }

      // Fetch banner image
      const bannerValue =
        activity.banner ??
        activity.banner_image ??
        activity.banner_url ??
        activity.image;
      if (bannerValue) {
        const [imageUrl, fallbackUrl] = await Promise.all([
          extractImageUrl(bannerValue),
          extractFallbackImageUrl(bannerValue),
        ]);
        if (imageUrl) {
          setBannerImage(toAbsoluteApiUrl(imageUrl) ?? imageUrl);
        }
        setBannerFallbackImage(
          fallbackUrl ? (toAbsoluteApiUrl(fallbackUrl) ?? fallbackUrl) : null
        );
      }

      // Fetch thumbnail image
      const thumbnailValue =
        activity.thumbnail ??
        activity.thumbnail_url ??
        activity.thumbnail_image;
      if (thumbnailValue) {
        const imageUrl = await extractImageUrl(thumbnailValue);
        if (imageUrl) {
          setThumbnailImage(toAbsoluteApiUrl(imageUrl) ?? imageUrl);
        }
      }

      // 🖇️ Resolve attachments (IDs -> Objects)
      const attachmentValues = activity.attachments;
      if (attachmentValues && Array.isArray(attachmentValues)) {
        const resolved = await mapWithConcurrency(
          attachmentValues,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (item: any) => {
            if (typeof item === "string") {
              // If it's a URL or path already
              if (item.startsWith("http") || item.includes("/")) {
                return { file_path: item, name: item.split("/").pop() };
              }
              // If it's a UUID
              try {
                const raw = await UploadService.getFileById({ id: item });
                const unwrapped = unwrapFileApiRecord(raw);
                const record = Array.isArray(unwrapped)
                  ? unwrapped[0]
                  : unwrapped;
                if (record && typeof record === "object") {
                  return record;
                }
                return (raw as object) ?? { id: item, name: "Attachment" };
              } catch {
                return { id: item, name: "Unknown File" };
              }
            }
            return item;
          }
        );
        const filtered = resolved.filter(Boolean);
        if (JSON.stringify(filtered) !== JSON.stringify(resolvedAttachments)) {
          setResolvedAttachments(filtered);
        }
      } else {
        setResolvedAttachments([]);
      }
    };

    fetchMedia();
  }, [activity, extractFallbackImageUrl, extractImageUrl, resolvedAttachments]);

  // Calculate activity status
  const getActivityStatus = (): "active" | "upcoming" | "completed" => {
    const startDateStr = activity?.startdate ?? activity?.start_date;
    const endDateStr = activity?.enddate ?? activity?.end_date;

    if (!startDateStr || !endDateStr) {
      return "upcoming";
    }

    const now = new Date();
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    if (now < startDate) {
      return "upcoming";
    }
    if (now >= startDate && now <= endDate) {
      return "active";
    }
    return "completed";
  };

  // Calculate days remaining
  const getDaysRemaining = (): number => {
    const endDateStr = activity?.enddate ?? activity?.end_date;
    if (!endDateStr) {
      return 0;
    }
    const now = new Date();
    const endDate = new Date(endDateStr);
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Calculate duration in days
  const getDuration = (): number => {
    const startDateStr = activity?.startdate ?? activity?.start_date;
    const endDateStr = activity?.enddate ?? activity?.end_date;

    if (!startDateStr || !endDateStr) {
      return 0;
    }
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);
    const diffTime = endDate.getTime() - startDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Count attachments
  const getAttachmentCount = (): number => {
    return resolvedAttachments.length;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Format as DD-MM-YYYY
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatLongDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  if (fetchError) {
    console.error("❌ Fetch error:", fetchError);
    return (
      <AdminLayout>
        <div className="py-12 text-center">
          <h2 className="text-2xl font-bold">Error Loading Activity</h2>
          <p className="mt-2 text-muted-foreground">
            {fetchError instanceof Error
              ? fetchError.message
              : "An error occurred"}
          </p>
          <Button
            className="mt-4"
            onClick={() => navigate("..", { relative: "path", replace: true })}
          >
            Back to Activities
          </Button>
        </div>
      </AdminLayout>
    );
  }

  if (!activity) {
    console.log("⚠️ Activity is null. id:", id, "activityData:", activityData);
    return (
      <AdminLayout>
        <div className="py-12 text-center">
          <h2 className="text-2xl font-bold">Activity Not Found</h2>
          <Button
            className="mt-4"
            onClick={() => navigate("..", { relative: "path", replace: true })}
          >
            Back to Activities
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const status = getActivityStatus();
  const daysRemaining = getDaysRemaining();
  const duration = getDuration();
  const attachmentCount = getAttachmentCount();
  const startDateStr = activity.startdate ?? activity.start_date;
  const endDateStr = activity.enddate ?? activity.end_date;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("..", { relative: "path", replace: true })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">{activity.title}</h1>
            <div className="mt-2 flex items-center gap-4 text-muted-foreground">
              {startDateStr && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(startDateStr)}</span>
                </div>
              )}
              {activity.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>{activity.location}</span>
                </div>
              )}
              <Badge
                className={`${
                  status === "completed"
                    ? "bg-amber-600"
                    : status === "active"
                      ? "bg-green-600"
                      : "bg-blue-600"
                } rounded-full border-0 px-2 py-0.5 text-xs font-normal text-white`}
              >
                {status}
              </Badge>
            </div>
          </div>
          <Button
            onClick={() => setEditDialogOpen(true)}
            className="bg-gradient-to-r from-purple-600 to-orange-500 text-white hover:opacity-90"
          >
            Edit Activity
          </Button>
        </div>

        {id && (
          <EditActivityDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            recordId={id}
            onUpdated={() => {
              queryClient.invalidateQueries({
                queryKey: ["activity", SCHEMA_NAME, id],
              });
            }}
          />
        )}

        {/* Banner Image */}
        {bannerImage && (
          <ResponsiveBanner
            src={bannerImage}
            fallbackSrc={bannerFallbackImage}
            alt={activity.title}
          />
        )}

        {/* Summary Statistics */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
                <div className="text-3xl font-bold">{daysRemaining}</div>
                <div className="text-sm text-muted-foreground">
                  Days Remaining
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
                  <Calendar className="h-6 w-6 text-orange-600" />
                </div>
                <div className="text-3xl font-bold">{duration} days</div>
                <div className="text-sm text-muted-foreground">Duration</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <Users className="h-6 w-6 text-gray-600" />
                </div>
                <div className="text-3xl font-bold">{attachmentCount}</div>
                <div className="text-sm text-muted-foreground">Attachments</div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-transparent">
            <TabsTrigger
              value="overview"
              className="data-[state=active]:bg-yellow-50 data-[state=active]:text-foreground"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="media"
              className="data-[state=active]:bg-yellow-50 data-[state=active]:text-foreground"
            >
              Media & Resources
            </TabsTrigger>
            <TabsTrigger
              value="guidelines"
              className="data-[state=active]:bg-yellow-50 data-[state=active]:text-foreground"
            >
              Submission Guidelines
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Description
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap leading-relaxed text-foreground">
                  {activity.description ?? "No description available."}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Event Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  {startDateStr && (
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Start Date
                      </p>
                      <p className="font-medium">
                        {formatLongDate(startDateStr)}
                      </p>
                    </div>
                  )}
                  {endDateStr && (
                    <div>
                      <p className="text-sm text-muted-foreground">End Date</p>
                      <p className="font-medium">
                        {formatLongDate(endDateStr)}
                      </p>
                    </div>
                  )}
                  {activity.location && (
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-medium">{activity.location}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge
                      className={`${
                        status === "completed"
                          ? "bg-amber-600"
                          : status === "active"
                            ? "bg-green-600"
                            : "bg-blue-600"
                      } rounded-full border-0 px-2 py-0.5 text-xs font-normal text-white`}
                    >
                      {status}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="media" className="space-y-6">
            {/* Thumbnail Image */}
            {thumbnailImage ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Activity Thumbnail
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-center">
                    <img
                      src={thumbnailImage}
                      alt={activity.title}
                      className="h-auto w-full max-w-md rounded-lg object-contain"
                    />
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No thumbnail image available
                </CardContent>
              </Card>
            )}

            {/* Attachments & Resources */}
            {resolvedAttachments.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="h-5 w-5" />
                    Attachments & Resources
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {resolvedAttachments.map((attachment: any, idx: number) => {
                      // Extract attachment URL and name
                      const attachmentUrl =
                        attachment.file_path ??
                        attachment.url ??
                        attachment.file_path_optimized ??
                        attachment.file_path_thumbnail;
                      const attachmentName =
                        attachment.file_original_name ??
                        attachment.name ??
                        attachment.filename ??
                        `Attachment ${idx + 1}`;
                      const hrefForLink =
                        toAbsoluteApiUrl(attachmentUrl ?? undefined) ?? "#";

                      // Determine file icon based on file type
                      const fileType =
                        attachment.file_type ?? attachment.type ?? "";
                      const isPdf =
                        fileType === "application/pdf" ||
                        attachmentName.toLowerCase().endsWith(".pdf");
                      const isExcel =
                        Boolean(fileType.includes("spreadsheet")) ||
                        Boolean(fileType.includes("excel")) ||
                        Boolean(
                          attachmentName.toLowerCase().endsWith(".xlsx")
                        ) ||
                        Boolean(attachmentName.toLowerCase().endsWith(".xls"));

                      return (
                        <a
                          key={
                            attachment.id ??
                            attachmentUrl ??
                            attachment.name ??
                            `attachment-${idx}`
                          }
                          href={hrefForLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-muted/50"
                          onClick={e => {
                            if (!attachmentUrl) {
                              return;
                            }
                            e.preventDefault();
                            void downloadFileWithAuth(
                              attachmentUrl,
                              attachmentName
                            ).catch(() => {
                              window.open(
                                hrefForLink,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            });
                          }}
                        >
                          {isPdf ? (
                            <FileText className="h-5 w-5 text-red-600" />
                          ) : isExcel ? (
                            <FileText className="h-5 w-5 text-green-600" />
                          ) : (
                            <FileText className="h-5 w-5 text-primary" />
                          )}
                          <span className="flex-1">{attachmentName}</span>
                          <Download className="h-4 w-4 text-muted-foreground" />
                        </a>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            {(!activity.attachments ||
              !Array.isArray(activity.attachments) ||
              activity.attachments.length === 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="h-5 w-5" />
                    Attachments & Resources
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No attachments available
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="guidelines" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Submission Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                      1
                    </div>
                    <div className="flex-1">
                      <h4 className="mb-1 font-semibold">
                        Detailed Description
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Provide at least 100 characters describing what was
                        done, who participated, and the impact of the activity.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                      2
                    </div>
                    <div className="flex-1">
                      <h4 className="mb-1 font-semibold">
                        Photo Documentation
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Upload at least 3 high-quality photos showing the
                        activity in progress. Each photo should be maximum 5MB.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                      3
                    </div>
                    <div className="flex-1">
                      <h4 className="mb-1 font-semibold">
                        Video Evidence (Optional)
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        You may upload a video file (max 100MB) showing
                        highlights of the activity.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                      4
                    </div>
                    <div className="flex-1">
                      <h4 className="mb-1 font-semibold">
                        Publication Details (Optional)
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        If the activity was covered by media, provide
                        publication details including media name, date, and
                        URL/scanned copy.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary/50 bg-primary/5">
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> All submissions will be reviewed by
                  evaluators. Make sure to provide complete and accurate
                  information for faster approval.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminActivityView;
